import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Phone } from "lucide-react";
import { Sidebar } from "@/components/Sidebar";
import { TabletSidebar } from "@/components/TabletSidebar";
import { MobileTopBar } from "@/components/mobile/MobileTopBar";
import { MobileBottomNav } from "@/components/mobile/MobileBottomNav";
import { MobileDialerFab } from "@/components/mobile/MobileDialerFab";
import { NumberProvider } from "@/context/NumberContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { ApiStatusBanner } from "@/components/ApiStatusBanner";
import { TotpGraceBanner } from "@/components/TotpGraceBanner";
import { TwoFactorSetup } from "@/components/TwoFactorSetup";
import { fetchMe } from "@/lib/api";
import { useIsMobile } from "@/hooks/useIsMobile";
import Dashboard from "@/pages/Dashboard";
import Calls from "@/pages/Calls";
import Contacts from "@/pages/Contacts";
import ContactDetail from "@/pages/ContactDetail";
import Settings from "@/pages/Settings";
import NumberSettings from "@/pages/NumberSettings";
import MyStationPage from "@/pages/MyStationPage";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";

// On mobile the home screen IS the recent calls list; on desktop it's the
// dashboard (admins and superviseurs — agents go straight to calls).
function Home() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const canSeeDashboard = user?.role === "admin" || user?.role === "superviseur";
  return isMobile || !canSeeDashboard ? <Navigate to="/appels" replace /> : <Dashboard />;
}

// Réglages is admin-only (also enforced by the backend with 403).
function AdminOnly({ children }) {
  const { isAdmin } = useAuth();
  return isAdmin ? children : <Navigate to="/appels" replace />;
}

const Splash = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="auth-loading">
    <span className="h-12 w-12 rounded-2xl bg-emerald-600 flex items-center justify-center animate-pulse">
      <Phone className="h-6 w-6 text-white" strokeWidth={2.2} />
    </span>
  </div>
);

// Full-page forced 2FA enrollment for admins past the 7-day grace period.
function ForceTotpPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="totp-forced-page">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-6">
          <span className="h-14 w-14 rounded-2xl bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-600/25">
            <Phone className="h-7 w-7 text-white" strokeWidth={2.2} />
          </span>
          <h1 className="font-heading font-bold text-slate-900 text-2xl tracking-tight mt-4">Sécurisez votre compte</h1>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <TwoFactorSetup
            mandatory
            onEnabled={() => {
              fetchMe().then((me) => { setUser(me); navigate("/", { replace: true }); }).catch(() => {});
            }}
          />
        </div>
      </div>
    </div>
  );
}

// Authenticated app shell. Every route below requires a valid session.
function Shell() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Splash />;
  if (!user) return <Navigate to="/connexion" replace state={{ from: location.pathname }} />;
  if (user.must_change_password && location.pathname !== "/mot-de-passe") {
    return <Navigate to="/mot-de-passe" replace />;
  }
  if (location.pathname === "/mot-de-passe") return <ChangePassword />;
  if (user.totp_setup_required) return <ForceTotpPage />;

  return (
    <NumberProvider>
      <ApiStatusBanner />
      <Sidebar />
      <TabletSidebar />

      <main className="md:ml-20 lg:ml-64 min-h-screen">
        <MobileTopBar />
        <div className="max-w-[1400px] mx-auto p-4 md:p-8 pb-36 md:pb-10">
          {(user.role === "admin" || user.role === "superviseur") && !user.totp_enabled && user.totp_grace_deadline && (
            <TotpGraceBanner deadline={user.totp_grace_deadline} />
          )}
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/appels" element={<Calls />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/contacts/:key" element={<ContactDetail />} />
            <Route path="/mon-poste" element={<MyStationPage />} />
            <Route path="/parametres" element={<AdminOnly><Settings /></AdminOnly>} />
            <Route path="/parametres/numeros/:id" element={<AdminOnly><NumberSettings /></AdminOnly>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>

      <MobileBottomNav />
      <MobileDialerFab />
    </NumberProvider>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-slate-50 text-slate-900">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/connexion" element={<Login />} />
            <Route path="/*" element={<Shell />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
